import React from 'react';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import cardValidator from 'card-validator';
import { Formik, Form, FieldArray } from 'formik';
import loadable from '@loadable/component';
import classNames from 'classnames';

import Checkbox from 'components/consumer/Checkbox';
import { ZIP_CODE_REGEX } from 'commons/constants';
import LabeledTextInput from 'components/consumer/LabeledTextInput';
import ToggleSwitch from 'components/consumer/ToggleSwitch';
import VisuallyHidden from 'components/consumer/VisuallyHidden';
import FormikFirstErrorFieldFocusOnSubmit from 'components/essentials/FormikFirstErrorFieldFocusOnSubmit';
import IconLock2 from 'components/icons/iconLock2';
import CreditCardExpiryField from './CreditCardExpiryField';
import CreditCardAmountField from './CreditCardAmountField';
import BillingAddressFields from './BillingAddressFields';
import ShippingInfoBlock from '../ShippingInfoBlock';
import CheckoutSubmitButton from '../../../CheckoutSubmitButton';

const AnimateHeight = loadable(() => import('react-animate-height'));

const BILLING_INITIAL_VALUES = {
  address: '',
  city: '',
  apartment: '',
  company: '',
  state: '',
  zipcode: '',
  phone_number: '',
};

export const CC_INITIAL_VALUES = {
  // name_on_card: '',
  number: '',
  expiry: '',
  cvc: '',
  amount: '',
  isBillingAddressSame: true,
  billingAddress: { ...BILLING_INITIAL_VALUES },
};

export const FORM_INITIAL_VALUES = {
  split_payment: false,
  credit_cards: [{ ...CC_INITIAL_VALUES }, { ...CC_INITIAL_VALUES }],
};

const billingAddressSchema = Yup.object().shape({
  address: Yup.string()
    .required('Street address is required')
    .min(6, 'Street Address must contain minimum six characters'),
  city: Yup.string().required('City is required'),
  apartment: Yup.string(),
  company: Yup.string(),
  state: Yup.string().required('State is required'),
  zipcode: Yup.string()
    .required('Zip code is required')
    .matches(ZIP_CODE_REGEX, 'Invalid zip code'),
  phone_number: Yup.string()
    .required('Please enter a valid phone number')
    .length(14, 'Please enter a valid phone number'),
});

const cardNumberMask = [
  /\d/,
  /\d/,
  /\d/,
  /\d/,
  ' ',
  /\d/,
  /\d/,
  /\d/,
  /\d/,
  ' ',
  /\d/,
  /\d/,
  /\d/,
  /\d/,
  ' ',
  /\d/,
  /\d/,
  /\d/,
  /\d/,
  /\d/,
  /\d/,
  /\d/,
];

const getCleanCardNumber = value => (value ? value.replace(/[^0-9]/g, '') : ''); // allow only digits

const getCleanCardAmount = value =>
  value ? parseFloat(value.toString().replace(/,/g, '')) : 0; // remove commas

const validateForm = values => {
  const errors = {};

  const credit_cards = values.split_payment
    ? values.credit_cards
    : [values.credit_cards[0]];

  credit_cards.forEach((cc, i) => {
    const error = {};

    const cardType = cardValidator.number(getCleanCardNumber(cc.number));
    if (!cc.number || !cardType || !cardType.isValid) {
      error.number = 'Please enter a valid card number';
    }

    if (!cc.expiry) {
      error.expiry = 'Expiry is required';
    } else {
      const res = cardValidator.expirationDate(cc.expiry);
      if (!res || !res.isValid) {
        error.expiry = 'Invalid expiry';
      }
    }

    if (!cc.cvc) {
      error.cvc = 'CVV is required';
    } else if (
      cardType &&
      cardType.card &&
      cardType.card.code &&
      cardType.card.code.size !== cc.cvc.length
    ) {
      error.cvc = `Invalid ${cardType.card.code.name}`;
    }

    if (!cc.isBillingAddressSame) {
      try {
        billingAddressSchema.validateSync(cc.billingAddress, {
          abortEarly: false,
        });
      } catch (e) {
        if (e && e.inner) {
          error.billingAddress = {};
          e.inner.forEach(err => {
            if (
              err.path &&
              !error.billingAddress[err.path] &&
              err.errors &&
              err.errors[0]
            ) {
              // eslint-disable-next-line prefer-destructuring
              error.billingAddress[err.path] = err.errors[0];
            }
          });
        }
      }
    }

    // capture error
    if (Object.keys(error).length) {
      errors.credit_cards = errors.credit_cards || [];
      errors.credit_cards[i] = error;
    }
  });

  return errors;
};

class CreditCardOption extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      cardTypes: [],
    };
    this.formik = React.createRef();
    this.initialValues = FORM_INITIAL_VALUES;
    const { split_payment, credit_cards } = this.props.payment;
    if (credit_cards) {
      this.initialValues = {
        split_payment,
        credit_cards,
      };
      this.isInitialValid = validateForm(this.initialValues);
    }
  }

  componentWillUnmount() {
    const { split_payment, credit_cards } =
      this.formik.current?.state?.values || {};

    this.props.updateCreditCard(split_payment, credit_cards);
  }

  render() {
    const {
      apartmentFieldPlaceholder = undefined,
      grandTotal = 0,
      hideBillingAddressCheckbox = false,
      hideSplitPayment = false,
      isInSidebarModal = false,
      placeOrderLoading,
      placeOrderWithCC,
      submitBtnRenderer = undefined,
      variant,
    } = this.props;

    return (
      <Formik
        ref={this.formik}
        initialValues={this.initialValues}
        isInitialValid={this.isInitialValid}
        validate={validateForm}
        onSubmit={values => {
          let credit_cards = [];

          if (!values.split_payment) {
            credit_cards = [values.credit_cards[0]];
          } else {
            values.credit_cards.forEach(credit_card => {
              const cc = { ...credit_card };
              if (cc.amount) {
                cc.amount = getCleanCardAmount(cc.amount);
                if (cc.amount > 0) {
                  credit_cards.push(cc);
                }
              }
            });
          }

          placeOrderWithCC(values.split_payment, credit_cards);
        }}
        render={({
          isValid,
          values,
          errors,
          touched,
          submitCount,
          handleSubmit,
          handleBlur,
          handleChange,
          setFieldValue,
          setFieldTouched,
        }) => (
          <Form
            onSubmit={handleSubmit}
            data-xc="credit-card-form"
            className="credit-card-option"
          >
            <div className="flex items-center text-sm text-gray mt-10 mx-0 mb-0 font-bold [&_svg_path]:fill-gray">
              <IconLock2 /> <span className="ml-2">Secure and encrypted</span>
            </div>
            <FieldArray
              name="credit_cards"
              render={() => (
                <>
                  <div className="mt-8">
                    {values.credit_cards.map((credit_card, index) => {
                      const cardTouched =
                        (touched.credit_cards && touched.credit_cards[index]) ||
                        {};
                      const cardErrors =
                        (errors.credit_cards && errors.credit_cards[index]) ||
                        {};
                      const cardType = this.state.cardTypes[index] || {};
                      const card = cardType.card || {};

                      return (
                        <AnimateHeight
                          // eslint-disable-next-line react/no-array-index-key
                          key={index}
                          duration={500}
                          height={
                            index === 0 || !!values.split_payment ? 'auto' : 0
                          }
                        >
                          <h2 className="text-lg font-bold bg-gray-light9 text-gray border border-solid border-gray-light7 [border-bottom:none] m-0 p-6">
                            Credit Card {values.split_payment ? index + 1 : ''}
                          </h2>

                          <div
                            className={classNames(
                              'border border-solid border-gray-light7 mb-6 p-4 lg:pt-10 lg:px-12 lg:pb-12',
                              {
                                'p-4': variant === 'ConvertToOrderModalUI',
                              }
                            )}
                          >
                            <AnimateHeight
                              duration={500}
                              height={
                                index > 0 || values.split_payment ? 'auto' : 0
                              }
                            >
                              <LabeledTextInput
                                id={`credit_cards.${index}.amount`}
                                name={`credit_cards.${index}.amount`}
                                component={CreditCardAmountField}
                                type="text"
                                data-xc={`credit_cards.${index}.amount`}
                                label="Amount to charge this card"
                                onChange={handleChange}
                                onBlur={evt => {
                                  const otherIndex = index === 1 ? 0 : 1;
                                  const amount = getCleanCardAmount(
                                    values.credit_cards[index].amount
                                  );

                                  if (amount <= 0) {
                                    setFieldValue(
                                      `credit_cards.${index}.amount`,
                                      0
                                    );
                                    setFieldValue(
                                      `credit_cards.${otherIndex}.amount`,
                                      grandTotal
                                    );
                                  }
                                  if (amount > grandTotal) {
                                    setFieldValue(
                                      `credit_cards.${index}.amount`,
                                      grandTotal
                                    );
                                    setFieldValue(
                                      `credit_cards.${otherIndex}.amount`,
                                      0
                                    );
                                  } else {
                                    setFieldValue(
                                      `credit_cards.${otherIndex}.amount`,
                                      grandTotal - amount
                                    );
                                  }

                                  handleBlur(evt);
                                }}
                                value={
                                  (credit_card.amount &&
                                    credit_card.amount.toString()) ||
                                  ''
                                }
                                errorMessage={
                                  cardTouched.amount && cardErrors.amount
                                    ? cardErrors.amount
                                    : null
                                }
                              />
                            </AnimateHeight>

                            <div
                              className={classNames({
                                'xl:flex': !isInSidebarModal,
                              })}
                            >
                              <div className="[&:not(.SidebarModal_*)]:xl:flex-1">
                                <LabeledTextInput
                                  id={`credit_cards.${index}.number`}
                                  name={`credit_cards.${index}.number`}
                                  mask={cardNumberMask}
                                  type="tel"
                                  data-xc={`credit_cards${index + 1}-number`}
                                  label="Card number"
                                  value={credit_card.number}
                                  onBlur={handleBlur}
                                  autoComplete="cc-number"
                                  autoCorrect="off"
                                  autoCapitalize="none"
                                  spellCheck="false"
                                  inputClassName={`credit_cards${index}number`}
                                  maxLength={
                                    card.safeMaxLength
                                      ? card.safeMaxLength + 3
                                      : 22
                                  }
                                  onChange={evt => {
                                    const updatedCardType = cardValidator.number(
                                      evt.target.value
                                    );
                                    this.setState(prevState => ({
                                      cardTypes: {
                                        ...prevState.cardTypes,
                                        [index]: updatedCardType,
                                      },
                                    }));
                                    handleChange(evt);
                                  }}
                                  errorMessage={
                                    cardTouched.number && cardErrors.number
                                      ? cardErrors.number
                                      : null
                                  }
                                  action={
                                    card?.niceType ? (
                                      <span className="bg-gray-light6 text-gray uppercase px-2 py-1 text-sm font-normal">
                                        {card.niceType}
                                      </span>
                                    ) : null
                                  }
                                />
                              </div>

                              <div
                                className={classNames('flex', '[&>*]:flex-1', {
                                  'xl:flex-[0.7] xl:ml-3 xl:max-w-[200px]': !isInSidebarModal,
                                  'last:xl:ml-3': !isInSidebarModal,
                                })}
                              >
                                <div className="min-w-[80px]">
                                  <LabeledTextInput
                                    id={`credit_cards.${index}.expiry`}
                                    name={`credit_cards.${index}.expiry`}
                                    component={CreditCardExpiryField}
                                    data-xc={`credit_cards${index + 1}-expiry`}
                                    label="MM/YY"
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    value={credit_card.expiry}
                                    inputClassName={`credit_cards${index}expiry`}
                                    autoComplete="cc-exp"
                                    autoCorrect="off"
                                    autoCapitalize="none"
                                    spellCheck="false"
                                    errorMessage={
                                      cardTouched.expiry && cardErrors.expiry
                                        ? cardErrors.expiry
                                        : null
                                    }
                                    ref={
                                      index === 1
                                        ? this.expiry1Ref
                                        : this.expiry0Ref
                                    }
                                  />
                                </div>

                                <div className="min-w-[65px] ml-3">
                                  <LabeledTextInput
                                    id={`credit_cards.${index}.cvc`}
                                    name={`credit_cards.${index}.cvc`}
                                    inputClassName={`credit_cards${index}cvc`}
                                    type="tel"
                                    data-xc={`credit_cards${index + 1}-cvc`}
                                    label={card.code ? card.code.name : 'CVV'}
                                    maxLength={card.code ? card.code.size : 3}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    value={credit_card.cvc}
                                    autoComplete="cc-csc"
                                    autoCorrect="off"
                                    autoCapitalize="none"
                                    spellCheck="false"
                                    errorMessage={
                                      cardTouched.cvc && cardErrors.cvc
                                        ? cardErrors.cvc
                                        : null
                                    }
                                  />
                                </div>
                              </div>
                            </div>

                            <div
                              className={classNames(
                                'billing-address-container',
                                {
                                  'mt-4': variant === 'ConvertToOrderModalUI',
                                }
                              )}
                            >
                              {!hideBillingAddressCheckbox ? (
                                <div
                                  className="max-[1023px]:mx-0 max-[1023px]:my-2"
                                  data-xc="billing-address-checkbox"
                                >
                                  <Checkbox
                                    onClick={() => {
                                      setFieldValue(
                                        `credit_cards.${index}.isBillingAddressSame`,
                                        !credit_card.isBillingAddressSame
                                      );
                                    }}
                                    checked={credit_card.isBillingAddressSame}
                                  >
                                    Billing address is same as shipping
                                  </Checkbox>
                                </div>
                              ) : (
                                <div className="text-base font-bold">
                                  Billing Address
                                </div>
                              )}

                              <AnimateHeight
                                duration={500}
                                height={
                                  !credit_card.isBillingAddressSame ||
                                  hideBillingAddressCheckbox
                                    ? 'auto'
                                    : 0
                                }
                              >
                                <BillingAddressFields
                                  id={`credit_cards.${index}.billingAddress`}
                                  errors={cardErrors.billingAddress || {}}
                                  touched={cardTouched.billingAddress || {}}
                                  values={credit_card.billingAddress}
                                  handleBlur={handleBlur}
                                  handleChange={handleChange}
                                  setFieldValue={setFieldValue}
                                  setFieldTouched={setFieldTouched}
                                  apartmentFieldPlaceholder={
                                    apartmentFieldPlaceholder
                                  }
                                  isRequired={!credit_card.isBillingAddressSame}
                                  variant={variant}
                                />
                              </AnimateHeight>
                            </div>
                          </div>
                        </AnimateHeight>
                      );
                    })}
                  </div>

                  {!hideSplitPayment && (
                    <div data-xc="pay-with-second-card">
                      <VisuallyHidden>
                        Selecting / deselecting the following checkbox will
                        update the content above.
                      </VisuallyHidden>

                      <ToggleSwitch
                        isChecked={!!values.split_payment}
                        onClick={() => {
                          if (!values.split_payment) {
                            setFieldValue('credit_cards', [
                              values.credit_cards[0],
                              {
                                ...CC_INITIAL_VALUES,
                                isBillingAddressSame:
                                  values.credit_cards[0].isBillingAddressSame,
                              },
                            ]);

                            setFieldValue('credit_cards.0.amount', grandTotal);

                            setFieldValue(
                              'split_payment',
                              !values.split_payment
                            );
                          } else {
                            setFieldValue(
                              'split_payment',
                              !values.split_payment
                            );

                            setTimeout(() => {
                              setFieldValue('credit_cards', [
                                values.credit_cards[0],
                                { ...CC_INITIAL_VALUES },
                              ]);
                            }, 500);
                          }
                        }}
                      >
                        Pay with 2 cards
                      </ToggleSwitch>
                    </div>
                  )}
                </>
              )}
            />

            {this.props.checkout && (
              <ShippingInfoBlock checkout={this.props.checkout} />
            )}

            {submitBtnRenderer ? (
              submitBtnRenderer({
                disabled: submitCount > 0 && !isValid,
              })
            ) : (
              <div className="flex justify-end my-[30px]">
                <CheckoutSubmitButton
                  dataXc="place-order-cc-btn"
                  disabled={(submitCount > 0 && !isValid) || placeOrderLoading}
                  isInSidebarModal={this.props.isInSidebarModal}
                  isLoading={placeOrderLoading}
                >
                  Submit order
                </CheckoutSubmitButton>
              </div>
            )}
            <FormikFirstErrorFieldFocusOnSubmit
              formFieldRefs={{}}
              arrayFields={['credit_cards']}
            />
          </Form>
        )}
      />
    );
  }
}

CreditCardOption.propTypes = {
  apartmentFieldPlaceholder: PropTypes.string,
  checkout: PropTypes.shape({
    shipping_address: PropTypes.shape({
      address1: PropTypes.string,
      address2: PropTypes.string,
      city: PropTypes.string,
      country: PropTypes.string,
      first_name: PropTypes.string,
      last_name: PropTypes.string,
      phone: PropTypes.string,
      state: PropTypes.string,
      zip: PropTypes.string,
    }),
  }),
  grandTotal: PropTypes.number,
  hideBillingAddressCheckbox: PropTypes.bool,
  hideSplitPayment: PropTypes.bool,
  isInSidebarModal: PropTypes.bool,
  payment: PropTypes.shape({
    apartmentFieldPlaceholder: PropTypes.string,
    credit_cards: PropTypes.arrayOf(
      PropTypes.shape({
        amount: PropTypes.number,
        cvc: PropTypes.string,
        expiry: PropTypes.string,
        number: PropTypes.string,
      })
    ),
    hideBillingAddressCheckbox: PropTypes.bool,
    hideSplitPayment: PropTypes.bool,
    placeOrderWithCC: PropTypes.func,
    split_payment: PropTypes.bool,
    submitBtnRenderer: PropTypes.func,
    updateCreditCard: PropTypes.func,
  }),
  placeOrderLoading: PropTypes.bool,
  placeOrderWithCC: PropTypes.func,
  submitBtnRenderer: PropTypes.func,
  updateCreditCard: PropTypes.func,
  variant: PropTypes.oneOf(['ConvertToOrderModalUI']),
};

export default CreditCardOption;
